// Mock-backend resolvers for the two "Фича аудит" features (Issues + Locations +
// Audits). These mirror the PG-server contract closely enough to drive the demo
// flows fully offline: triage, manual reports, corrective tasks, the audit run
// lifecycle, and the reporting views (results-by-location + answer stats).
//
// The server owns scoring and the audit_finding Issue; we replicate just enough of
// that here (completeAuditRun computes score/percentage/passed and raises an Issue
// on failure) so the demo behaves like production. See resolvers.ts for the base
// registry — these are merged in there.

import {getMockDb, updateMockDb, MockDb} from './mockDb';
import {getCurrentPersonaId} from './fixtures';

type Resolver = (variables: any) => any;

const listResult = (items: unknown[]) => ({items, nextToken: null, startedAt: Date.now()});
const nowIso = () => new Date().toISOString();
const uid = () => crypto.randomUUID();
const me = () => getCurrentPersonaId();

/*===================== generic patch helpers =====================*/

function patch(collection: keyof MockDb, id: string, fields: Record<string, any>): any {
    let updated: any = null;
    updateMockDb(db => {
        const list = db[collection] as any[];
        const idx = list.findIndex(row => row.id === id);
        if (idx === -1) return;
        list[idx] = {...list[idx], ...fields, version: (list[idx].version ?? 0) + 1, updatedAt: nowIso()};
        updated = list[idx];
    });
    return updated;
}

function softDelete(collection: keyof MockDb, id: string): any {
    return patch(collection, id, {deletedAt: nowIso()});
}

/*===================== Issues =====================*/

export function newIssue(input: any): any {
    const now = nowIso();
    const db = getMockDb();
    return {
        __typename: 'Issue',
        id: uid(),
        ownerId: db.client.id,
        workspaceId: input.workspaceId,
        source: input.source ?? 'manual_report',
        status: 'open',
        title: input.title ?? '',
        description: input.description ?? null,
        images: input.images ?? null,
        taskListUnitId: input.taskListUnitId ?? null,
        locationId: input.locationId ?? null,
        taskId: input.taskId ?? null,
        taskReportId: input.taskReportId ?? null,
        auditRunId: input.auditRunId ?? null,
        sourceRef: input.sourceRef ?? null,
        // Manual reports are attributed to the reporting user; the server does the
        // same. Default to the active demo persona so "My reports" populates.
        reportedBy: input.reportedBy ?? me(),
        overdueCount: input.overdueCount ?? 0,
        lastSignalAt: now,
        resolutionType: null,
        correctiveTaskId: null,
        resolutionNote: null,
        dismissComment: null,
        resolvedBy: null,
        resolvedAt: null,
        archivedAt: null,
        reopenedBy: null,
        reopenedAt: null,
        version: 1,
        createdAt: now,
        updatedAt: now,
        createdBy: me(),
        updatedBy: me(),
        deletedAt: null,
    };
}

const issueResolvers: Record<string, Resolver> = {
    listIssues: (v) => {
        const db = getMockDb();
        let rows = db.issues.filter(i => i.workspaceId === v?.workspaceId && !i.deletedAt);
        if (v?.source) rows = rows.filter(i => i.source === v.source);
        if (v?.status) rows = rows.filter(i => i.status === v.status);
        if (v?.locationId) rows = rows.filter(i => i.locationId === v.locationId);
        if (v?.taskListUnitId) rows = rows.filter(i => i.taskListUnitId === v.taskListUnitId);
        if (v?.reportedBy) rows = rows.filter(i => i.reportedBy === v.reportedBy);
        rows = [...rows].sort((a, b) => (b.lastSignalAt ?? b.createdAt).localeCompare(a.lastSignalAt ?? a.createdAt));
        return listResult(rows);
    },
    getIssue: (v) => getMockDb().issues.find(i => i.id === v?.id) ?? null,

    createIssue: (v) => {
        if (!v?.input) return null;
        const issue = newIssue(v.input);
        updateMockDb(db => { db.issues.unshift(issue); });
        return issue;
    },
    updateIssue: (v) => {
        const input = v?.input;
        if (!input?.id) return null;
        const {id, ...rest} = input;
        return patch('issues', id, rest);
    },
    resolveIssueWithNote: (v) => {
        const input = v?.input;
        if (!input?.id) return null;
        return patch('issues', input.id, {
            status: 'resolved',
            resolutionType: 'note',
            resolutionNote: input.note ?? null,
            resolvedBy: me(),
            resolvedAt: nowIso(),
            // A fresh resolution supersedes any earlier reopen — clear the marker so
            // the activity history doesn't show a stale "Reopened by" entry.
            reopenedBy: null,
            reopenedAt: null,
        });
    },
    resolveIssueWithCorrectiveTask: (v) => {
        const input = v?.input;
        if (!input?.id) return null;
        return patch('issues', input.id, {
            status: 'resolved',
            resolutionType: 'corrective_task',
            correctiveTaskId: input.correctiveTaskId ?? null,
            resolvedBy: me(),
            resolvedAt: nowIso(),
            reopenedBy: null,
            reopenedAt: null,
        });
    },
    dismissIssue: (v) => {
        const input = v?.input;
        if (!input?.id) return null;
        return patch('issues', input.id, {
            status: 'dismissed',
            dismissComment: input.comment ?? null,
            resolvedBy: me(),
            resolvedAt: nowIso(),
            reopenedBy: null,
            reopenedAt: null,
        });
    },
    // Resolved/dismissed → open. Resolution fields (resolutionType/resolutionNote/
    // dismissComment/resolvedBy/resolvedAt) are left in place — they become the
    // "previous outcome" entry in the reopened issue's activity history.
    reopenIssue: (v) => (v?.id ? patch('issues', v.id, {
        status: 'open',
        reopenedBy: me(),
        reopenedAt: nowIso(),
    }) : null),
    archiveIssue: (v) => (v?.id ? patch('issues', v.id, {archivedAt: nowIso()}) : null),
    unarchiveIssue: (v) => (v?.id ? patch('issues', v.id, {archivedAt: null}) : null),
    deleteIssue: (v) => (v?.id ? softDelete('issues', v.id) : null),
};

/*===================== Locations =====================*/

const locationResolvers: Record<string, Resolver> = {
    listLocations: (v) => {
        const db = getMockDb();
        let rows = db.locations.filter(l => l.workspaceId === v?.workspaceId && !l.deletedAt);
        if (!v?.includeArchived) rows = rows.filter(l => !l.archived);
        return listResult(rows);
    },
    getLocation: (v) => getMockDb().locations.find(l => l.id === v?.id) ?? null,

    createLocation: (v) => {
        const input = v?.input;
        if (!input) return null;
        const now = nowIso();
        const row = {
            __typename: 'Location',
            id: uid(),
            ownerId: getMockDb().client.id,
            workspaceId: input.workspaceId,
            name: input.name ?? '',
            address: input.address ?? null,
            archived: false,
            mainTaskListUnitId: null,
            version: 1,
            createdAt: now,
            updatedAt: now,
            createdBy: me(),
            updatedBy: me(),
            deletedAt: null,
        };
        updateMockDb(db => { db.locations.push(row); });
        return row;
    },
    updateLocation: (v) => {
        const input = v?.input;
        if (!input?.id) return null;
        const {id, ...rest} = input;
        return patch('locations', id, rest);
    },
    deleteLocation: (v) => (v?.id ? softDelete('locations', v.id) : null),
};

/*===================== Audits — templates + questions =====================*/

const auditTemplateResolvers: Record<string, Resolver> = {
    listAudits: (v) => {
        const db = getMockDb();
        let rows = db.auditTemplates.filter(t => t.workspaceId === v?.workspaceId && !t.deletedAt);
        if (v?.publicationState) rows = rows.filter(t => t.publicationState === v.publicationState);
        rows = [...rows].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
        return listResult(rows);
    },
    getAudit: (v) => getMockDb().auditTemplates.find(t => t.id === v?.id) ?? null,

    createAudit: (v) => {
        const input = v?.input;
        if (!input) return null;
        const now = nowIso();
        const row = {
            __typename: 'AuditTemplate',
            id: uid(),
            ownerId: getMockDb().client.id,
            workspaceId: input.workspaceId,
            name: input.name ?? '',
            description: input.description ?? null,
            requiredPassPercentage: input.requiredPassPercentage ?? 80,
            publicationState: input.publicationState ?? 'draft',
            questionsCount: 0,
            questionsOrder: input.questionsOrder ?? [],
            version: 1,
            createdAt: now,
            updatedAt: now,
            createdBy: me(),
            updatedBy: me(),
            deletedAt: null,
        };
        updateMockDb(db => { db.auditTemplates.unshift(row); });
        return row;
    },
    updateAudit: (v) => {
        const input = v?.input;
        if (!input?.id) return null;
        const {id, ...rest} = input;
        return patch('auditTemplates', id, rest);
    },
    deleteAudit: (v) => (v?.id ? softDelete('auditTemplates', v.id) : null),

    listAuditQuestions: (v) => {
        const db = getMockDb();
        const rows = db.auditQuestions.filter(q => q.auditTemplateId === v?.auditTemplateId && !q.deletedAt);
        const order: string[] | undefined = db.auditTemplates.find(t => t.id === v?.auditTemplateId)?.questionsOrder;
        if (order?.length) {
            const rank = (id: string) => { const i = order.indexOf(id); return i < 0 ? Number.MAX_SAFE_INTEGER : i; };
            rows.sort((a, b) => rank(a.id) - rank(b.id));
        }
        return rows; // this query returns a bare list, not a {items} connection
    },

    createAuditQuestion: (v) => {
        const input = v?.input;
        if (!input) return null;
        const now = nowIso();
        const row = {
            __typename: 'AuditQuestion',
            id: uid(),
            ownerId: getMockDb().client.id,
            workspaceId: input.workspaceId,
            auditTemplateId: input.auditTemplateId,
            text: input.text ?? '',
            points: input.points ?? 0,
            photoRequiredToPass: input.photoRequiredToPass ?? false,
            reference: input.reference ?? null,
            version: 1,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
        };
        updateMockDb(db => {
            db.auditQuestions.push(row);
            const tIdx = db.auditTemplates.findIndex(t => t.id === input.auditTemplateId);
            if (tIdx !== -1) {
                const t = db.auditTemplates[tIdx];
                const order = Array.isArray(t.questionsOrder) ? [...t.questionsOrder, row.id] : [row.id];
                db.auditTemplates[tIdx] = {...t, questionsOrder: order, questionsCount: order.length, updatedAt: now};
            }
        });
        return row;
    },
    updateAuditQuestion: (v) => {
        const input = v?.input;
        if (!input?.id) return null;
        const {id, ...rest} = input;
        return patch('auditQuestions', id, rest);
    },
    deleteAuditQuestion: (v) => {
        if (!v?.id) return null;
        const removed = softDelete('auditQuestions', v.id);
        if (removed) {
            updateMockDb(db => {
                const tIdx = db.auditTemplates.findIndex(t => t.id === removed.auditTemplateId);
                if (tIdx !== -1) {
                    const t = db.auditTemplates[tIdx];
                    const order = (t.questionsOrder ?? []).filter((qid: string) => qid !== v.id);
                    db.auditTemplates[tIdx] = {...t, questionsOrder: order, questionsCount: order.length, updatedAt: nowIso()};
                }
            });
        }
        return removed;
    },
};

/*===================== Audits — run flow + reporting =====================*/

const auditRunResolvers: Record<string, Resolver> = {
    listAuditRuns: (v) => {
        const db = getMockDb();
        let rows = db.auditRuns.filter(r => r.workspaceId === v?.workspaceId && !r.deletedAt);
        if (v?.auditTemplateId) rows = rows.filter(r => r.auditTemplateId === v.auditTemplateId);
        if (v?.locationId) rows = rows.filter(r => r.locationId === v.locationId);
        rows = [...rows].sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''));
        return listResult(rows);
    },
    getAuditRun: (v) => getMockDb().auditRuns.find(r => r.id === v?.id) ?? null,

    listAuditAnswers: (v) =>
        getMockDb().auditAnswers.filter(a => a.auditRunId === v?.auditRunId), // bare list

    // Latest completed run per location — the "Результаты" view.
    listAuditResultsByLocation: (v) => {
        const runs = getMockDb().auditRuns.filter(
            r => r.auditTemplateId === v?.auditTemplateId && r.status === 'completed' && !r.deletedAt,
        );
        const byLoc = new Map<string, any>();
        for (const r of runs) {
            const key = r.locationId ?? '__none__';
            const cur = byLoc.get(key);
            if (!cur || (r.completedAt ?? '').localeCompare(cur.completedAt ?? '') > 0) byLoc.set(key, r);
        }
        return listResult([...byLoc.values()]);
    },

    // Yes/No distribution per question across all of a template's runs — the
    // "Статистика" view. Returns a bare list of {auditQuestionId, totalAnswers, yesCount, noCount}.
    auditAnswerStats: (v) => {
        const db = getMockDb();
        const runIds = new Set(
            db.auditRuns.filter(r => r.auditTemplateId === v?.auditTemplateId && !r.deletedAt).map(r => r.id),
        );
        const answers = db.auditAnswers.filter(a => runIds.has(a.auditRunId));
        const byQ = new Map<string, {total: number; yes: number; no: number}>();
        for (const a of answers) {
            let stat = byQ.get(a.auditQuestionId);
            if (!stat) { stat = {total: 0, yes: 0, no: 0}; byQ.set(a.auditQuestionId, stat); }
            stat.total += 1;
            if (a.answer) stat.yes += 1; else stat.no += 1;
        }
        return [...byQ.entries()].map(([auditQuestionId, s]) => ({
            auditQuestionId,
            totalAnswers: s.total,
            yesCount: s.yes,
            noCount: s.no,
        }));
    },

    // Top-level cross-audit/cross-location progress table (Audits landing page).
    // Mirrors the server's listAuditProgress: published templates x non-archived
    // locations, latest completed run per pair, open audit_finding issue count.
    // Assignment fields are always null — the mock backend has no audit_assignment
    // store yet (assignments aren't demo-wired), matching today's demo behavior.
    listAuditProgress: (v) => {
        const db = getMockDb();
        const templates = db.auditTemplates.filter(
            t => t.workspaceId === v?.workspaceId && !t.deletedAt && t.publicationState === 'published'
                && (!v?.auditTemplateId || t.id === v.auditTemplateId),
        );
        const locations = db.locations.filter(
            l => l.workspaceId === v?.workspaceId && !l.deletedAt && !l.archived
                && (!v?.locationId || l.id === v.locationId),
        );

        const rows: any[] = [];
        for (const t of templates) {
            for (const l of locations) {
                const runs = db.auditRuns.filter(
                    r => r.auditTemplateId === t.id && r.locationId === l.id && r.status === 'completed' && !r.deletedAt,
                );
                const lastRun = runs.reduce((best: any, r) => (
                    !best || (r.completedAt ?? '').localeCompare(best.completedAt ?? '') > 0 ? r : best
                ), null);
                const openIssueCount = lastRun
                    ? db.issues.filter(i => i.auditRunId === lastRun.id && i.status === 'open' && !i.deletedAt).length
                    : 0;
                rows.push({
                    __typename: 'AuditProgressRow',
                    auditTemplateId: t.id,
                    auditTemplateName: t.name,
                    locationId: l.id,
                    locationName: l.name,
                    lastRunId: lastRun?.id ?? null,
                    lastCompletedAt: lastRun?.completedAt ?? null,
                    percentage: lastRun?.percentage ?? null,
                    passed: lastRun?.passed ?? null,
                    score: lastRun?.score ?? null,
                    maxScore: lastRun?.maxScore ?? null,
                    openIssueCount,
                    assignmentStatus: null,
                    assignedTo: null,
                });
            }
        }
        rows.sort((a, b) => a.auditTemplateName.localeCompare(b.auditTemplateName) || a.locationName.localeCompare(b.locationName));
        return rows;
    },

    startAuditRun: (v) => {
        const input = v?.input;
        if (!input) return null;
        const now = nowIso();
        const row = {
            __typename: 'AuditRun',
            id: uid(),
            ownerId: getMockDb().client.id,
            workspaceId: input.workspaceId,
            auditTemplateId: input.auditTemplateId,
            locationId: input.locationId ?? null,
            auditorId: me(),
            status: 'in_progress',
            score: null,
            maxScore: null,
            percentage: null,
            passed: null,
            startedAt: now,
            completedAt: null,
            version: 1,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
        };
        updateMockDb(db => { db.auditRuns.unshift(row); });
        return row;
    },

    // answer/photos/comment can each arrive independently (a Yes/No click, a photo
    // upload, and a comment blur are three separate saves for the same row) — merge
    // against the existing row so an omitted field never clobbers one set earlier.
    saveAuditAnswer: (v) => {
        const input = v?.input;
        if (!input) return null;
        const db = getMockDb();
        const question = db.auditQuestions.find(q => q.id === input.auditQuestionId);
        if (!question) return null;
        const points = Number(question.points) || 0;
        const photoRequiredToPass = !!question.photoRequiredToPass;

        const existing = db.auditAnswers.find(
            a => a.auditRunId === input.auditRunId && a.auditQuestionId === input.auditQuestionId,
        );
        const answer = input.answer !== undefined && input.answer !== null ? input.answer : existing?.answer ?? null;
        const photos = input.photos !== undefined && input.photos !== null ? input.photos : existing?.photos ?? [];
        const comment = input.comment !== undefined && input.comment !== null ? input.comment : existing?.comment ?? null;

        if (answer === true && photoRequiredToPass && !(Array.isArray(photos) && photos.length > 0)) {
            throw new Error('A photo is required before marking Yes for this question');
        }
        const score = answer ? points : 0;

        const now = nowIso();
        let saved: any = null;
        updateMockDb(d => {
            const idx = d.auditAnswers.findIndex(
                a => a.auditRunId === input.auditRunId && a.auditQuestionId === input.auditQuestionId,
            );
            if (idx === -1) {
                saved = {
                    __typename: 'AuditAnswer',
                    id: uid(),
                    ownerId: d.client.id,
                    workspaceId: input.workspaceId,
                    auditRunId: input.auditRunId,
                    auditQuestionId: input.auditQuestionId,
                    answer,
                    score,
                    photos,
                    comment,
                    createdAt: now,
                    updatedAt: now,
                };
                d.auditAnswers.push(saved);
            } else {
                saved = {...d.auditAnswers[idx], answer, score, photos, comment, updatedAt: now};
                d.auditAnswers[idx] = saved;
            }
        });
        return saved;
    },

    // Server-owned: compute score/percentage/passed, then raise an audit_finding
    // Issue on failure. We replicate both so the demo matches production.
    completeAuditRun: (v) => {
        if (!v?.id) return null;
        const db = getMockDb();
        const run = db.auditRuns.find(r => r.id === v.id);
        if (!run) return null;
        const template = db.auditTemplates.find(t => t.id === run.auditTemplateId);
        const questions = db.auditQuestions.filter(q => q.auditTemplateId === run.auditTemplateId && !q.deletedAt);
        const answers = db.auditAnswers.filter(a => a.auditRunId === run.id);
        const score = answers.reduce((sum, a) => sum + (Number(a.score) || 0), 0);
        const maxScore = questions.reduce((sum, q) => sum + (Number(q.points) || 0), 0);
        const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 100;
        const required = template?.requiredPassPercentage ?? 80;
        const passed = percentage >= required;
        const now = nowIso();
        // Mirrors the server: a row with answer == null (photo/comment saved but no
        // Yes/No pick) scores like a No — these counts distinguish that from an
        // actually-answered No so a completed run isn't ambiguous.
        const totalQuestions = questions.length;
        const answeredCount = answers.filter(a => a.answer !== null && a.answer !== undefined).length;

        const updated = patch('auditRuns', run.id, {
            status: 'completed', score, maxScore, percentage, passed, answeredCount, totalQuestions, completedAt: now,
        });

        if (!passed) {
            const locationName = db.locations.find(l => l.id === run.locationId)?.name;
            const issue = newIssue({
                workspaceId: run.workspaceId,
                source: 'audit_finding',
                title: `${template?.name ?? 'Audit'} failed${locationName ? ` at ${locationName}` : ''} (${percentage}%)`,
                description: `Audit scored ${score}/${maxScore} (${percentage}%), below the required ${required}%.`,
                locationId: run.locationId,
                auditRunId: run.id,
                sourceRef: {percentage, requiredPassPercentage: required, score, maxScore},
                reportedBy: null,
            });
            updateMockDb(d => { d.issues.unshift(issue); });
        }
        return updated;
    },
};

export const reliabilityAuditResolvers: Record<string, Resolver> = {
    ...issueResolvers,
    ...locationResolvers,
    ...auditTemplateResolvers,
    ...auditRunResolvers,
};
